import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Plus, PencilSimpleLine, Trash, ClipboardText } from "@phosphor-icons/react";
import api, { formatApiError } from "@/lib/api";
import { useAddons } from "@/contexts/AddonsContext";
import { Alert, AlertDescription } from "@/components/ui/alert";
import CompensationLineItems from "@/components/workforce/CompensationLineItems";

/**
 * Settings → Payroll Templates tab (Phase 4-b).
 *
 * Templates are named, reusable sets of compensation line items with
 * default labels + amounts. Admins and HR Rule Managers can create /
 * edit / delete. The Employee Master edit dialog then offers a
 * "Select template" dropdown that populates all line items in one
 * click (with a confirmation prompt when it would overwrite existing
 * lines — that guard lives in CompensationLineItems).
 */
export default function PayrollTemplatesTab() {
  const { hasEnterpriseWorkforce } = useAddons();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);   // {name, description, model, lines}
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/compensation-templates");
      setItems(data || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { if (hasEnterpriseWorkforce) load(); else setLoading(false); }, [hasEnterpriseWorkforce]);

  if (!hasEnterpriseWorkforce) {
    return (
      <Alert>
        <AlertDescription>
          Payroll Templates require the Enterprise Workforce Suite add-on. Activate it under Settings → Subscription to enable this tab.
        </AlertDescription>
      </Alert>
    );
  }

  const startNew  = () => setEditing({ name: "", description: "", model: "monthly", lines: [] });
  const startEdit = (t) => setEditing({ ...t, lines: t.lines || [] });
  const close     = () => setEditing(null);

  const save = async () => {
    if (!editing.name.trim()) { toast.error("Give the template a name"); return; }
    setSaving(true);
    try {
      if (editing.id) await api.put(`/compensation-templates/${editing.id}`, editing);
      else            await api.post("/compensation-templates", editing);
      toast.success(editing.id ? "Template updated" : "Template saved");
      close(); load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const remove = async (t) => {
    if (!window.confirm(`Delete template "${t.name}"?`)) return;
    try {
      await api.delete(`/compensation-templates/${t.id}`);
      toast.success("Template deleted");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <Card data-testid="payroll-templates-tab">
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle className="flex items-center gap-2">
            <ClipboardText size={18} weight="duotone" className="text-primary" />
            Payroll Templates
          </CardTitle>
          <p className="text-xs text-muted-foreground mt-1">
            Reusable compensation line-item bundles. Applied in one click from the Employee Master.
          </p>
        </div>
        <Button onClick={startNew} data-testid="new-template-btn">
          <Plus size={16} className="mr-1" /> New template
        </Button>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="p-6 text-center text-muted-foreground text-sm">Loading templates…</div>
        ) : items.length === 0 ? (
          <div className="p-8 text-center text-muted-foreground text-sm" data-testid="templates-empty">
            No templates yet. Create one to speed up onboarding new hires.
          </div>
        ) : (
          <div className="divide-y">
            {items.map(t => (
              <div key={t.id} className="py-3 flex items-center gap-3" data-testid={`template-row-${t.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="font-medium">{t.name}</div>
                  {t.description && <div className="text-xs text-muted-foreground">{t.description}</div>}
                  <div className="text-xs text-muted-foreground mt-0.5">
                    Model: <b>{t.model}</b> · {t.lines?.length || 0} line item{(t.lines?.length || 0) === 1 ? "" : "s"}
                  </div>
                </div>
                <Button variant="ghost" size="sm" onClick={() => startEdit(t)} data-testid={`template-edit-${t.id}`}>
                  <PencilSimpleLine size={16} />
                </Button>
                <Button variant="ghost" size="sm" onClick={() => remove(t)} className="text-rose-600" data-testid={`template-delete-${t.id}`}>
                  <Trash size={16} />
                </Button>
              </div>
            ))}
          </div>
        )}
      </CardContent>

      {editing && (
        <Dialog open onOpenChange={(v) => !v && close()}>
          <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="template-dialog">
            <DialogHeader>
              <DialogTitle>{editing.id ? "Edit template" : "New template"}</DialogTitle>
              <DialogDescription>
                Save a reusable set of compensation line items. Admins pick this in the Employee Master.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label>Template name</Label>
                  <Input value={editing.name} onChange={e => setEditing({ ...editing, name: e.target.value })} data-testid="template-name" />
                </div>
                <div>
                  <Label>Description</Label>
                  <Textarea rows={1} value={editing.description || ""} onChange={e => setEditing({ ...editing, description: e.target.value })} data-testid="template-desc" />
                </div>
              </div>

              <CompensationLineItems
                model={editing.model}
                setModel={v => setEditing({ ...editing, model: v })}
                lines={editing.lines || []}
                setLines={arr => setEditing({ ...editing, lines: arr })}
                showTemplates={false}
                dataTestidPrefix="template-comp"
              />
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={close} disabled={saving}>Cancel</Button>
              <Button onClick={save} disabled={saving} data-testid="template-save-btn">
                {saving ? "Saving…" : (editing.id ? "Save changes" : "Create template")}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </Card>
  );
}
